/**
 * Tours de novedades puntuales — separados de ALL_TOURS / tour completo por página.
 * Claves localStorage NO usan prefijo tour_ para no mezclarse con resetAllTours.
 */
import { shouldShowTour } from './tourSteps'

/** Snooze en sessionStorage: oculta el aviso en esta sesión sin marcar la novedad como vista */
export const NEWS_EDITOR_VIEWER_ZOOM_SNOOZE_KEY = 'app_news_editor_viewer_zoom_snooze_session'

export const NEWS_EDITOR_VIEWER_ZOOM_KEY = 'app_news_editor_viewer_zoom_v1'

/** Tour corto: mejoras de visor PDF y zoom (editor) */
export const NEWS_EDITOR_VIEWER_ZOOM = {
  id: 'news-editor-viewer-zoom-v1',
  storageKey: NEWS_EDITOR_VIEWER_ZOOM_KEY,
  version: 'v1',
  label: 'Novedades: visor y zoom',
  steps: [
    {
      targetId: 'tour-news-pdf-scroll',
      title: '📐 Visor del PDF',
      description:
        'El área del documento ahora se muestra mejor contenida: puedes desplazarte con scroll cuando el PDF es más ancho que la pantalla y el contenido queda centrado al cargar o al cambiar de página.',
      position: 'bottom',
    },
    {
      targetId: 'tour-news-pdf-zoom',
      title: '🔍 Zoom ampliado',
      description:
        'Puedes acercar y alejar más el documento con + y −. El zoom conserva el centro de lo que estabas viendo para una experiencia más estable al ajustar el tamaño.',
      position: 'bottom',
    },
  ],
}

export const NEWS_HISTORIAL_PAGINADO_ID = 'historial-paginado-2026-10'
export const NEWS_HISTORIAL_PAGINADO_VERSION = 'historial-paginado-2026-10'
export const NEWS_HISTORIAL_PAGINADO_KEY_PREFIX = 'app_news_historial_paginado_2026_10'

export function claveNovedadHistorialPaginado(userId) {
  return `${NEWS_HISTORIAL_PAGINADO_KEY_PREFIX}:${userId}`
}

export function clavePausaNovedadHistorialPaginado(userId) {
  return `${NEWS_HISTORIAL_PAGINADO_KEY_PREFIX}_snooze:${userId}`
}

export function usuarioEnSesionId() {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user')
    if (!raw) return null
    const id = JSON.parse(raw)?.id
    if (id == null || id === '') return null
    return String(id)
  } catch {
    return null
  }
}

/** Tour de esta actualización. La clave se completa con el usuario antes de leer o guardar. */
export const NEWS_HISTORIAL_PAGINADO = {
  id: NEWS_HISTORIAL_PAGINADO_ID,
  storageKey: NEWS_HISTORIAL_PAGINADO_KEY_PREFIX,
  version: NEWS_HISTORIAL_PAGINADO_VERSION,
  label: 'Novedades: historial paginado',
  steps: [
    {
      page: '/dashboard',
      targetId: 'tour-news-historial-paginado-aviso',
      title: 'PDFSigner tiene nuevas mejoras',
      description:
        'Optimizamos las consultas de documentos y la navegación para que revisar Historial General y Firmados y Pendientes sea mucho más rápido.',
      position: 'bottom',
    },
    {
      page: '/historial-general',
      targetId: 'tour-news-historial-list',
      title: 'Historial por páginas',
      description:
        'Historial General ahora carga los documentos por páginas, haciendo mucho más rápida la consulta incluso cuando existen miles de registros.',
      position: 'top',
    },
    {
      page: '/historial-general',
      targetId: 'tour-news-historial-search',
      title: 'Buscador',
      description:
        'El buscador ahora consulta directamente la información necesaria y evita cargar todos los documentos en el navegador.',
      position: 'bottom',
    },
    {
      page: '/historial-general',
      targetId: 'tour-news-historial-pagination',
      title: 'Páginas del historial',
      description:
        'Puedes navegar entre páginas manteniendo visible únicamente la información que necesitas.',
      position: 'top',
    },
    {
      page: '/firmados-pendientes',
      targetId: 'tour-news-signed-list',
      title: 'Firmados y Pendientes',
      description:
        'Firmados y Pendientes también fue optimizado. Ahora carga por páginas y responde más rápido al consultar tus documentos.',
      position: 'bottom',
    },
    {
      page: '/firmados-pendientes',
      targetId: 'tour-news-signed-pagination',
      title: 'Páginas de Firmados y Pendientes',
      description:
        'Usa Anterior y Siguiente para recorrer los documentos sin cargar todo el historial de una sola vez.',
      position: 'top',
    },
    {
      page: '/firmados-pendientes',
      title: '¡Listo!',
      description: '¡Listo! Ya conoces las mejoras de esta actualización.',
      position: 'bottom',
    },
  ],
}

export function prepararNovedadHistorialPaginado(userId) {
  if (userId == null || userId === '') return null
  NEWS_HISTORIAL_PAGINADO.storageKey = claveNovedadHistorialPaginado(userId)
  return NEWS_HISTORIAL_PAGINADO
}

export function novedadHistorialPaginadoPendiente(userId) {
  const tour = prepararNovedadHistorialPaginado(userId)
  if (!tour) return false
  return shouldShowNewsTour(tour)
}

export function pausarNovedadHistorialPaginado(userId) {
  try {
    sessionStorage.setItem(clavePausaNovedadHistorialPaginado(userId), '1')
  } catch {
    /* ignore */
  }
}

export function novedadHistorialPaginadoPausada(userId) {
  if (userId == null || userId === '') return false
  try {
    return sessionStorage.getItem(clavePausaNovedadHistorialPaginado(userId)) === '1'
  } catch {
    return false
  }
}

export const NEWS_FIRMA_GUIADA_ID = 'firma-guiada-notas-v2-2026-10'
export const NEWS_FIRMA_GUIADA_VERSION = 'firma-guiada-notas-v2-2026-10'
export const NEWS_FIRMA_GUIADA_KEY_PREFIX = 'app_news_firma_guiada_notas_v2_2026_10'

export function claveNovedadFirmaGuiada(userId) {
  return `${NEWS_FIRMA_GUIADA_KEY_PREFIX}:${userId}`
}

export function clavePausaNovedadFirmaGuiada(userId) {
  return `${NEWS_FIRMA_GUIADA_KEY_PREFIX}_snooze:${userId}`
}

export const NEWS_FIRMA_GUIADA = {
  id: NEWS_FIRMA_GUIADA_ID,
  storageKey: NEWS_FIRMA_GUIADA_KEY_PREFIX,
  version: NEWS_FIRMA_GUIADA_VERSION,
  label: 'Novedades: firma guiada y notas',
  steps: [
    {
      page: '/dashboard',
      targetId: 'tour-news-firma-guiada-aviso',
      title: 'PDFSigner tiene nuevas mejoras',
      description:
        'Ahora puedes definir campos de firma, agregar notas trazables y visualizar documentos antes de firmar.',
      position: 'bottom',
    },
    {
      page: '/editor',
      targetId: 'tour-upload-button',
      title: 'Campos obligatorios de firma',
      description:
        'Los usuarios con permisos para preparar documentos ahora pueden arrastrar cada firmante sobre el PDF y definir exactamente dónde debe firmar.',
      position: 'bottom',
    },
    {
      page: '/editor',
      targetId: 'tour-observaciones',
      title: 'Notas trazables',
      description:
        'Puedes escribir una nota, colocarla sobre el documento y dejar registrado quién la escribió y cuándo. La nota queda en el PDF firmado.',
      position: 'left',
    },
    {
      page: '/documentos-pendientes',
      targetId: 'tour-pending-header',
      title: 'Visualizar desde Mis Pendientes',
      description:
        'Desde Mis Pendientes puedes abrir el documento en solo lectura. Verlo no firma, no rechaza y no cambia el turno.',
      position: 'bottom',
    },
    {
      page: '/firmados-pendientes',
      targetId: 'tour-signed-header',
      title: 'Visualizar en Firmados y Pendientes',
      description:
        'El icono de visualizar abre el documento en solo lectura desde Firmados y Pendientes, sin cambiar su estado.',
      position: 'bottom',
    },
    {
      page: '/historial-general',
      targetId: 'tour-news-historial-list',
      title: 'Visualizar desde el historial',
      description:
        'Quien puede abrir Historial General también puede visualizar el documento en solo lectura desde esa lista.',
      position: 'top',
    },
    {
      page: '/dashboard',
      targetId: 'tour-news-firma-guiada-aviso',
      title: '¡Listo!',
      description: 'Ya conoces las mejoras de esta actualización.',
      position: 'bottom',
    },
  ],
}

export function prepararNovedadFirmaGuiada(userId) {
  if (userId == null || userId === '') return null
  NEWS_FIRMA_GUIADA.storageKey = claveNovedadFirmaGuiada(userId)
  return NEWS_FIRMA_GUIADA
}

export function novedadFirmaGuiadaPendiente(userId) {
  const tour = prepararNovedadFirmaGuiada(userId)
  if (!tour) return false
  return shouldShowNewsTour(tour)
}

export function pausarNovedadFirmaGuiada(userId) {
  try {
    sessionStorage.setItem(clavePausaNovedadFirmaGuiada(userId), '1')
  } catch {
    /* ignore */
  }
}

export function novedadFirmaGuiadaPausada(userId) {
  if (userId == null || userId === '') return false
  try {
    return sessionStorage.getItem(clavePausaNovedadFirmaGuiada(userId)) === '1'
  } catch {
    return false
  }
}

export const ALL_NEWS_TOURS = {
  [NEWS_EDITOR_VIEWER_ZOOM.id]: NEWS_EDITOR_VIEWER_ZOOM,
  [NEWS_HISTORIAL_PAGINADO.id]: NEWS_HISTORIAL_PAGINADO,
  [NEWS_FIRMA_GUIADA.id]: NEWS_FIRMA_GUIADA,
}

export function shouldShowNewsTour(newsTour) {
  return shouldShowTour(newsTour)
}

/** Marca la novedad como vista sin abrir el tour (botón Cerrar del aviso) */
export function dismissNewsTourWithoutViewing(newsTour) {
  if (!newsTour?.storageKey) return
  try {
    localStorage.setItem(newsTour.storageKey, String(newsTour.version ?? 'v1'))
  } catch {
    /* ignore */
  }
}

export function setNewsEditorViewerZoomSnooze() {
  try {
    sessionStorage.setItem(NEWS_EDITOR_VIEWER_ZOOM_SNOOZE_KEY, '1')
  } catch {
    /* ignore */
  }
}

export function isNewsEditorViewerZoomSnoozed() {
  try {
    return sessionStorage.getItem(NEWS_EDITOR_VIEWER_ZOOM_SNOOZE_KEY) === '1'
  } catch {
    return false
  }
}
